import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import {
  Children,
  createContext,
  Fragment,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { twJoin } from "tailwind-merge";

import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { IconButton } from "@/design-system/IconButton";
import { isPortalDropdownTarget } from "@/helpers/portal-dropdown";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";
import { Dialog as ArkModal, Portal as ArkPortal } from "@/ui-adapters/ArkDialog";
import { RuntimeModalHeight } from "@/ui-adapters/RuntimeModalHeight";

export type ModalBaseProps = {
  isOpened: boolean;
  onOpenChange: (state: boolean) => void;
  onExitComplete?: () => void;
};

export type ModalProps = ModalBaseProps & {
  children: React.ReactNode;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  descriptionTone?: "primary" | "secondary";
  badgeComponent?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
  showClose?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnClickOutside?: boolean;
  isNested?: boolean;
  isPending?: boolean;
  "data-testid"?: string;
  ids?: {
    root?: string;
    title?: string;
    description?: string;
  };
  height?: number;
  shouldFocusContentOnOpen?: boolean;
  /** Use fade for content that measures its size when it mounts. */
  animation?: "zoom" | "fade";
};

const ModalSizeContext = createContext<NonNullable<ModalProps["size"]>>("md");
const ModalHasHeaderContext = createContext(false);

function ModalRoot({
  isOpened,
  onOpenChange,
  children,
  title,
  description,
  descriptionTone = "secondary",
  badgeComponent,
  size = "md",
  "data-testid": dataTestId,
  ids,
  height,
  isPending = false,
  showClose = true,
  shouldCloseOnEsc = true,
  shouldCloseOnClickOutside = true,
  isNested = false,
  onExitComplete,
  shouldFocusContentOnOpen,
  animation = "zoom",
}: ModalProps): React.ReactNode {
  const { t } = useTranslation();
  const refContainer = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const wasOpenedRef = useRef(isOpened);

  useEffect(() => {
    if (wasOpenedRef.current && !isOpened) {
      onExitComplete?.();
    }

    wasOpenedRef.current = isOpened;
  }, [isOpened, onExitComplete]);

  useLayoutEffect(() => {
    const el = refContainer.current;
    if (!el) return;
    if (isOpened) {
      el.removeAttribute("inert");
    } else {
      el.setAttribute("inert", "");
    }
  }, [isOpened]);

  const DescriptionElement = typeof description === "string" ? "p" : "div";
  const hasHeader = Boolean(showClose || badgeComponent || title || description);

  return (
    <ArkModal.Root
      open={isOpened}
      onOpenChange={(details) => onOpenChange(details.open)}
      closeOnEscape={shouldCloseOnEsc}
      closeOnInteractOutside={shouldCloseOnClickOutside}
      initialFocusEl={shouldFocusContentOnOpen ? () => contentRef.current : undefined}
      onInteractOutside={(event) => {
        // Prevent modal from closing when clicking on portal dropdowns
        if (isPortalDropdownTarget(event.detail.originalEvent.target)) {
          event.preventDefault();
        }
      }}
      unmountOnExit
      lazyMount
    >
      {isOpened && (
        <ArkPortal>
          <ArkModal.Backdrop
            className={twJoin(
              "fixed top-0 left-0 h-dvh w-dvw cursor-pointer bg-background-default-inverse/80",
              "data-[state=open]:animate-in data-[state=open]:fade-in",
              isNested ? "z-overlay" : "z-backdrop",
            )}
          />
          <ArkModal.Positioner
            className={twJoin(
              "fixed top-0 left-0 flex size-full items-center justify-center px-space-md",
              isNested ? "z-modal" : "z-overlay",
            )}
          >
            <ArkModal.Content
              ref={contentRef}
              data-testid={dataTestId}
              id={ids?.root}
              className={twJoin(
                "@container relative h-auto max-h-screen-minus-8 w-full rounded-xl bg-background-default sm:max-h-screen-minus-16",
                "overflow-hidden data-[state=open]:animate-in data-[state=open]:fade-in",
                animation === "zoom" && "data-[state=open]:zoom-in",
                "focus:outline-hidden",
                size === "sm" && "sm:max-w-overlay-sm",
                size === "md" && "sm:max-w-overlay-md",
                size === "lg" && "sm:max-w-overlay-lg",
                size === "xl" && "sm:max-w-overlay-xl",
                size === "fullscreen" && "h-full xl:max-w-content-wide",
              )}
            >
              <RuntimeModalHeight height={height}>
                {(runtimeHeightAttributes) => (
                  <div {...runtimeHeightAttributes} ref={refContainer} className="flex w-full flex-1 flex-col">
                    {hasHeader && (
                      <div
                        className={twResolve(
                          "flex h-fit w-full items-start justify-between gap-space-md bg-background-default p-space-lg",
                          (size === "fullscreen" || height) &&
                            "sticky top-0 z-navigation border-b border-border-default",
                        )}
                      >
                        <section className="flex min-h-control-sm flex-1 flex-col justify-center gap-dialog-content">
                          {badgeComponent && <span className="flex">{badgeComponent}</span>}
                          {title && (
                            <ArkModal.Title asChild>
                              <h3 id={ids?.title} className="text-heading-3 text-text-primary">
                                {title}
                              </h3>
                            </ArkModal.Title>
                          )}
                          {description && (
                            <ArkModal.Description asChild>
                              <DescriptionElement
                                id={ids?.description}
                                className={descriptionTone === "primary" ? "text-text-primary" : "text-text-secondary"}
                              >
                                {description}
                              </DescriptionElement>
                            </ArkModal.Description>
                          )}
                        </section>
                        {showClose && (
                          <ArkModal.CloseTrigger asChild>
                            <IconButton
                              data-testid="modal-close"
                              variant="soft"
                              title={t("common.action.close")}
                              withTooltip={false}
                              size="sm"
                              isCircular
                              icon={<XCloseIcon />}
                            />
                          </ArkModal.CloseTrigger>
                        )}
                      </div>
                    )}
                    <ModalLoading isVisible={isPending} />
                    <ModalHasHeaderContext.Provider value={hasHeader}>
                      <ModalSizeContext.Provider value={size}>{children}</ModalSizeContext.Provider>
                    </ModalHasHeaderContext.Provider>
                  </div>
                )}
              </RuntimeModalHeight>
            </ArkModal.Content>
          </ArkModal.Positioner>
        </ArkPortal>
      )}
    </ArkModal.Root>
  );
}

interface ModalActionsProps {
  children: React.ReactNode[] | React.ReactNode;
  isSticky?: boolean;
}

function ModalActions({ children, isSticky }: ModalActionsProps): React.ReactNode {
  const size = useContext(ModalSizeContext);
  const stretchButtons = (size === "sm" || size === "md") && countActionChildren(children) <= 2;

  return (
    <footer
      className={twResolve(
        "flex w-full shrink-0 flex-col-reverse flex-wrap gap-dialog-content px-space-lg pt-space-md pb-space-lg sm:flex-row sm:items-center sm:justify-end [&_button]:w-full sm:[&_button]:w-fit [&>a]:w-full sm:[&>a]:w-fit",
        isSticky && "z-raised mt-0 border-t border-border-default",
        stretchButtons && "sm:[&>a]:min-w-0 sm:[&>a]:flex-1 sm:[&>button]:min-w-0 sm:[&>button]:flex-1",
      )}
    >
      {children}
    </footer>
  );
}

function countActionChildren(children: React.ReactNode): number {
  let count = 0;

  for (const child of Children.toArray(children)) {
    if (isValidElement<{ children?: React.ReactNode }>(child) && child.type === Fragment) {
      count += countActionChildren(child.props.children);
    } else {
      count += 1;
    }
  }

  return count;
}

interface ModalBodyProps {
  children: React.ReactNode[] | React.ReactNode;
  centered?: boolean;
  withPadding?: boolean;
}
function ModalBody({ centered, children, withPadding = true }: ModalBodyProps): React.ReactNode {
  const hasHeader = useContext(ModalHasHeaderContext);

  return (
    <section
      className={twJoin(
        "flex size-full max-h-full flex-col overflow-auto",
        centered && "items-center justify-center",
        withPadding && (hasHeader ? "px-space-lg pb-space-lg" : "p-space-lg"),
      )}
    >
      {children}
    </section>
  );
}

interface ModalCloseProps {
  children: React.ReactNode;
}

function ModalClose({ children }: ModalCloseProps): React.ReactNode {
  return <ArkModal.CloseTrigger asChild>{children}</ArkModal.CloseTrigger>;
}

interface ModalLoaderProps {
  isVisible: boolean;
}

function ModalLoading({ isVisible }: ModalLoaderProps): React.ReactNode {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 z-sticky flex items-center justify-center bg-background-default"
        >
          <FullSizeLoader size="sm" noDelay />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const Modal = {
  Root: ModalRoot,
  Body: ModalBody,
  Actions: ModalActions,
  Close: ModalClose,
  Loading: ModalLoading,
};

type ModalStateData<TData> =
  | (TData extends never
      ? { isOpen: true }
      : {
          data: TData;
          isOpen: true;
        })
  | {
      data?: TData;
      isOpen: false;
    };

interface ModalHelpers<TData> {
  requestClose: () => void;
  afterClose: () => void;
  open: (data: TData) => void;
}

export type ModalState<TData = never> = ModalStateData<TData> & ModalHelpers<TData>;

/**
 * Keeps modal data available until the dialog has unmounted, then resets it through afterClose.
 */
export function useModalState<TData = never>(initialData?: ModalStateData<TData>): ModalState<TData> {
  const [state, setState] = useState<ModalStateData<TData>>(
    initialData || {
      isOpen: false,
      data: undefined,
    },
  );

  const requestClose = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const afterClose = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false, data: undefined }));
  }, []);

  const open = useCallback((data: TData) => {
    setState({ isOpen: true as any, data });
  }, []);

  return useMemo(
    () => ({
      isOpen: state.isOpen,
      data: (state as any).data,
      requestClose,
      afterClose,
      open,
    }),
    [afterClose, requestClose, open, state],
  );
}
